import type { DocMigration } from "../docMigration";

/** Inline snapshot matching built-in sheathing defaults — migration stays isolated from app defaults. */
const builtinSheathingComponentsMerged = {
  ["defaultSheathing"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "defaultSheathing",
    name: '3/4" Plywood',
    sheathingType: "PLYWOOD",
    thickness: 3 / 4,
    profile: "3/4-u",
    spanRating: "SR16",
    sortIndex: 0,
  },
  ["1/4-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "1/4-drywall",
    name: '1/4" Drywall',
    sheathingType: "DRYWALL",
    thickness: 1 / 4,
    sortIndex: 1,
  },
  ["3/8-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "3/8-drywall",
    name: '3/8" Drywall',
    sheathingType: "DRYWALL",
    thickness: 3 / 8,
    sortIndex: 2,
  },
  ["1/2-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "1/2-drywall",
    name: '1/2" Drywall',
    sheathingType: "DRYWALL",
    thickness: 1 / 2,
    sortIndex: 3,
  },
  ["5/8-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "5/8-drywall",
    name: '5/8" Drywall',
    sheathingType: "DRYWALL",
    thickness: 5 / 8,
    sortIndex: 4,
  },
  ["7/16-osb"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "7/16-osb",
    name: '7/16" OSB',
    sheathingType: "OSB",
    thickness: 7 / 16,
    profile: "7/16",
    spanRating: "SR16",
    sortIndex: 5,
  },
};

const builtInSheathingComponentsGroupId = "builtInSheathingComponentsGroup";

const builtInSheathingComponentsGroup = {
  entityType: "GROUP",
  id: builtInSheathingComponentsGroupId,
  name: "Built-in Sheathing Components",
  sortIndex: 7,
  isBuiltIn: true,
};

/** Primary keys of `builtinSheathingComponentsMerged`. */
const builtInSheathingComponentIds = [
  "defaultSheathing",
  "1/4-drywall",
  "3/8-drywall",
  "1/2-drywall",
  "5/8-drywall",
  "7/16-osb",
] as const;

/** Match `defaultWallInteriorSheathingComponentId` / `defaultWallExteriorSheathingComponentId` in app defaults. */
const defaultWallInteriorSheathingComponentId = "1/2-drywall";
const defaultWallExteriorSheathingComponentId = "7/16-osb";
const defaultSheathingComponentFallbackId = "defaultSheathing";

/** Matches docDefault / builtInMasonryComponentsGroupId — keep migration isolated from app defaults. */
const builtInMasonryComponentsGroupId = "builtInMasonryComponentsGroup";

const builtInMasonryComponentsGroup = {
  entityType: "GROUP",
  id: builtInMasonryComponentsGroupId,
  name: "Built-in Masonry Components",
  sortIndex: 8,
  isBuiltIn: true,
};

/** Built-in CMU primary keys merged by migration_20260512_add_masonry_components_doc_table. */
const builtInMasonryComponentIds = ["cmu-4", "cmu-6", "cmu-8", "cmu-10", "cmu-12"] as const;

function mergeBuiltinSheathingComponents(json: any): void {
  const existing =
    json.sheathingComponents !== undefined &&
    json.sheathingComponents !== null &&
    typeof json.sheathingComponents === "object" &&
    !Array.isArray(json.sheathingComponents)
      ? json.sheathingComponents
      : {};
  json.sheathingComponents = {
    ...builtinSheathingComponentsMerged,
    ...existing,
  };
}

/**
 * Older wall finish assemblies can reference sheathing IDs that were never on the doc or were removed.
 * After merging the built-in catalog, remap missing FKs using the same defaults as built-in wall assemblies.
 */
function repairDanglingWallPlaneSheathingComponentIds(json: any): void {
  const sheathingTable =
    json.sheathingComponents !== undefined &&
    json.sheathingComponents !== null &&
    typeof json.sheathingComponents === "object" &&
    !Array.isArray(json.sheathingComponents)
      ? json.sheathingComponents
      : null;

  if (sheathingTable === null) {
    return;
  }

  if (json.wallPlaneAssemblies === undefined || json.wallPlaneAssemblies === null) {
    return;
  }
  if (typeof json.wallPlaneAssemblies !== "object" || Array.isArray(json.wallPlaneAssemblies)) {
    return;
  }

  const resolveReplacementId = (layer: any): string => {
    const finishType = layer?.finish?.finishType;
    if (finishType === "INTERIOR") {
      return defaultWallInteriorSheathingComponentId;
    }
    if (finishType === "EXTERIOR") {
      return defaultWallExteriorSheathingComponentId;
    }
    return defaultSheathingComponentFallbackId;
  };

  for (const assemblyId of Object.keys(json.wallPlaneAssemblies)) {
    const assembly = json.wallPlaneAssemblies[assemblyId];
    if (assembly === undefined || assembly === null || typeof assembly !== "object") {
      continue;
    }
    const layers = assembly.layers;
    if (layers === undefined || layers === null || typeof layers !== "object" || Array.isArray(layers)) {
      continue;
    }

    let layersChanged = false;
    const newLayers = { ...layers };

    for (const layerId of Object.keys(newLayers)) {
      const layer = newLayers[layerId];
      if (layer === undefined || layer === null || typeof layer !== "object") {
        continue;
      }
      if (layer.layerType !== "SHEATHING") {
        continue;
      }

      const sid = layer.sheathingComponentId;
      if (typeof sid === "string" && sheathingTable[sid] !== undefined) {
        continue;
      }

      const replacementId = resolveReplacementId(layer);
      if (sheathingTable[replacementId] === undefined) {
        continue;
      }

      newLayers[layerId] = { ...layer, sheathingComponentId: replacementId };
      layersChanged = true;
    }

    if (layersChanged) {
      json.wallPlaneAssemblies[assemblyId] = { ...assembly, layers: newLayers };
    }
  }
}

function applyBuiltInSheathingEntityGroups(json: any): void {
  const sheathingTable =
    json.sheathingComponents !== undefined &&
    json.sheathingComponents !== null &&
    typeof json.sheathingComponents === "object" &&
    !Array.isArray(json.sheathingComponents)
      ? json.sheathingComponents
      : null;

  if (sheathingTable === null) {
    return;
  }

  const presentBuiltInIds = builtInSheathingComponentIds.filter((id) => sheathingTable[id] !== undefined);
  if (presentBuiltInIds.length === 0) {
    return;
  }

  if (json.groups === undefined || json.groups === null || typeof json.groups !== "object") {
    json.groups = {};
  }

  if (json.groups[builtInSheathingComponentsGroupId] === undefined) {
    json.groups[builtInSheathingComponentsGroupId] = { ...builtInSheathingComponentsGroup };
  }

  if (json.entityGroups === undefined || json.entityGroups === null || typeof json.entityGroups !== "object") {
    json.entityGroups = {};
  }

  for (const componentId of builtInSheathingComponentIds) {
    if (sheathingTable[componentId] === undefined) {
      continue;
    }
    const entityGroupKey = `sheathingComponent.${componentId}`;
    if (json.entityGroups[entityGroupKey] === undefined) {
      json.entityGroups[entityGroupKey] = builtInSheathingComponentsGroupId;
    }
  }
}

function applyBuiltInMasonryEntityGroups(json: any): void {
  const masonryTable =
    json.masonryComponents !== undefined &&
    json.masonryComponents !== null &&
    typeof json.masonryComponents === "object" &&
    !Array.isArray(json.masonryComponents)
      ? json.masonryComponents
      : null;

  if (masonryTable === null) {
    return;
  }

  const presentBuiltInIds = builtInMasonryComponentIds.filter((id) => masonryTable[id] !== undefined);
  if (presentBuiltInIds.length === 0) {
    return;
  }

  if (json.groups === undefined || json.groups === null || typeof json.groups !== "object") {
    json.groups = {};
  }

  if (json.groups[builtInMasonryComponentsGroupId] === undefined) {
    json.groups[builtInMasonryComponentsGroupId] = { ...builtInMasonryComponentsGroup };
  }

  if (json.entityGroups === undefined || json.entityGroups === null || typeof json.entityGroups !== "object") {
    json.entityGroups = {};
  }

  for (const componentId of builtInMasonryComponentIds) {
    if (masonryTable[componentId] === undefined) {
      continue;
    }
    const entityGroupKey = `masonryComponent.${componentId}`;
    if (json.entityGroups[entityGroupKey] === undefined) {
      json.entityGroups[entityGroupKey] = builtInMasonryComponentsGroupId;
    }
  }
}

const migration: DocMigration = {
  id: "20260513_masonry_components_builtin_group",
  description:
    "Merges built-in sheathing catalog entries; repairs wall plane sheathing FKs; ensures built-in sheathing and masonry CMUs are grouped (groups + entityGroups)",
  apply(json: any, _warnings: string[], _errors: string[]): any {
    mergeBuiltinSheathingComponents(json);
    repairDanglingWallPlaneSheathingComponentIds(json);
    applyBuiltInSheathingEntityGroups(json);
    applyBuiltInMasonryEntityGroups(json);
    return json;
  },
};

export default migration;
